// src-lit/components/product/kd-variant-button.ts
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';

/**
 * @element kd-variant-button
 * @description バリアント選択に応じてカートボタンの状態と価格表示を更新する。
 *   kd-product-variant-selected イベントを購読し、
 *   available に応じてボタンの disabled/text を切り替え、
 *   price / compareAtPrice で価格 DOM を更新する。
 */
@customElement('kd-variant-button')
export class KdVariantButton extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** セクションID（フォーム・価格要素の特定に使用） */
  @property({ type: String, attribute: 'section-id' })
  sectionId = '';

  private unsubscribe: (() => void) | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    this.updateAddButton(detail.available);
    this.updatePrice(detail);
  }

  /**
   * Add to Cart ボタンの有効/無効を切り替える
   */
  private updateAddButton(available: boolean): void {
    const productForm = document.getElementById(`product-form-${this.sectionId}`);
    if (!productForm) return;

    const addButton = productForm.querySelector<HTMLButtonElement>('[name="add"]');
    const addButtonText = productForm.querySelector<HTMLSpanElement>('[name="add"] > span');
    if (!addButton) return;

    if (available) {
      addButton.removeAttribute('disabled');
      if (addButtonText) {
        addButtonText.textContent = window.variantStrings?.addToCart ?? '';
      }
    } else {
      addButton.setAttribute('disabled', 'disabled');
      if (addButtonText) {
        addButtonText.textContent = window.variantStrings?.soldOut ?? '';
      }
    }
  }

  /**
   * 価格表示を更新する
   */
  private updatePrice(detail: VariantSelectedDetail): void {
    const priceEl = document.getElementById(`price-${this.sectionId}`);
    if (!priceEl) return;

    priceEl.classList.remove('visibility-hidden');

    const regularPriceEl = priceEl.querySelector<HTMLElement>('[data-regular-price]');
    if (regularPriceEl) {
      regularPriceEl.textContent = this.formatPrice(detail.price);
    }

    const comparePriceEl = priceEl.querySelector<HTMLElement>('[data-compare-price]');
    if (comparePriceEl) {
      if (detail.compareAtPrice && detail.compareAtPrice > detail.price) {
        comparePriceEl.textContent = this.formatPrice(detail.compareAtPrice);
        comparePriceEl.classList.remove('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.classList.add('hidden');
      }
    }
  }

  /**
   * 価格を日本円フォーマットに変換する
   */
  private formatPrice(priceInCents: number): string {
    return `¥${Math.floor(priceInCents / 100).toLocaleString()}`;
  }
}

declare global {
  interface Window {
    variantStrings?: {
      addToCart: string;
      soldOut: string;
      unavailable: string;
    };
  }
  interface HTMLElementTagNameMap {
    'kd-variant-button': KdVariantButton;
  }
}
